import React, { useState, useEffect } from 'react';
import { Plus, Search, Trash2, Edit3, Check, X, FlaskConical, Pill, ClipboardList, Stethoscope, Loader2, Save, ExternalLink, Layers } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ClinicalMetadataSetup = ({ type }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [labCats, setLabCats] = useState([]);

    const config = {
        'lab-tests': { 
            title: 'Laboratory Tests', 
            icon: FlaskConical, 
            endpoint: 'lab-tests', 
            fields: [
                { name: 'testName', label: 'Test Name', flex: 'flex-1' }, 
                { name: 'category', label: 'Category', type: 'select', options: labCats, width: 'w-40' }, 
                { name: 'price', label: 'Price', type: 'number', width: 'w-32' }
            ] 
        },
        'lab-categories': { 
            title: 'Test Categories', 
            icon: Layers, 
            endpoint: 'lab-categories', 
            fields: [
                { name: 'name', label: 'Category Name', flex: 'flex-1' }
            ] 
        },
        'medicines': { 
            title: 'Medicines / Drugs', 
            icon: Pill, 
            endpoint: 'medicines', 
            fields: [
                { name: 'name', label: 'Drug Name', flex: 'flex-1' }, 
                { name: 'strength', label: 'Strength', width: 'w-40' }, 
                { name: 'form', label: 'Form', type: 'select', options: ['Tablet', 'Capsule', 'Syrup', 'Injection'], width: 'w-40' }
            ] 
        },
        'diagnosis': { 
            title: 'ICD-10 Diagnosis', 
            icon: ClipboardList, 
            endpoint: 'diagnosis', 
            fields: [
                { name: 'code', label: 'ICD Code', width: 'w-32' }, 
                { name: 'name', label: 'Diagnosis Name', flex: 'flex-1' }
            ] 
        },
        'complaints': { 
            title: 'Clinical Complaints', 
            icon: Stethoscope, 
            endpoint: 'complaints', 
            fields: [
                { name: 'name', label: 'Complaint Name', flex: 'flex-1' }
            ] 
        }
    }[type];

    const API_BASE = `${window.location.protocol}//${window.location.hostname}/api/clinical`;

    useEffect(() => {
        fetchMetadata();
        fetchItems();
    }, [type]);

    const fetchMetadata = async () => {
        if (type === 'lab-tests') {
            try {
                const res = await axios.get(`${API_BASE}/lab-categories`);
                setLabCats(res.data.map(c => c.name));
            } catch (err) { console.error(err); }
        }
    };

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/${config.endpoint}`);
            setItems(res.data);
        } catch (err) { toast.error('Fetch failed'); }
        setLoading(false);
    };

    const handleUpdate = async (id, updatedData) => {
        try {
            await axios.put(`${API_BASE}/${config.endpoint}/${id}`, updatedData);
        } catch (err) { toast.error('Update failed'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await axios.delete(`${API_BASE}/${config.endpoint}/${id}`);
            toast.success('Deleted');
            fetchItems();
        } catch (err) { toast.error('Delete failed'); }
    };

    const handleAdd = async () => {
        try {
            const defaultObj = {};
            const uniqueSuffix = Math.floor(Math.random() * 1000);
            config.fields.forEach(f => {
                if (f.name === 'price') defaultObj[f.name] = 0;
                else if (f.type === 'select') defaultObj[f.name] = f.options[0] || '';
                else defaultObj[f.name] = `New ${f.label} ${uniqueSuffix}`;
            });
            await axios.post(`${API_BASE}/${config.endpoint}`, defaultObj);
            toast.success('New record created');
            fetchItems();
        } catch (err) { toast.error('Add failed: ' + (err.response?.data?.error || err.message)); }
    };

    const filteredItems = items.filter(i => 
        Object.values(i).some(val => String(val).toLowerCase().includes(search.toLowerCase()))
    );

    if (loading && items.length === 0) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Loading {config.title}...</p>
        </div>
    );

    return (
        <div className="p-8 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Elegant Header */}
            <div className="bg-white/80 backdrop-blur-xl px-10 py-8 rounded-[48px] border border-slate-200/60 shadow-2xl shadow-slate-200/40 flex justify-between items-center mb-10 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-indigo-500 via-sky-400 to-indigo-500" />
                <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-indigo-600 rounded-[28px] flex items-center justify-center shadow-2xl shadow-indigo-200 rotate-3 group-hover:rotate-0 transition-transform duration-500">
                        <config.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-800 tracking-tight">{config.title}</h1>
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1 italic">Clinical Data Configuration</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative w-64 hidden md:block">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                        <input 
                            type="text" 
                            placeholder="Find records..."
                            className="w-full bg-slate-50 border-none rounded-2xl py-3 pl-12 pr-4 text-[11px] font-bold text-slate-600 ring-1 ring-slate-100 outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={handleAdd}
                        className="px-8 py-4 bg-indigo-600 hover:bg-slate-900 text-white rounded-3xl text-[11px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 transition-all active:scale-95 flex items-center gap-3"
                    >
                        <Plus className="w-4 h-4" /> Add Record
                    </button>
                </div>
            </div>

            {/* In-Line Editing Table */}
            <div className="bg-white rounded-[48px] border border-slate-200/60 shadow-xl overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            {config.fields.map(f => (
                                <th key={f.name} className={`px-10 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ${f.width || ''}`}>{f.label}</th>
                            ))}
                            <th className="px-10 py-6 text-right text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] w-24">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {filteredItems.map((item) => (
                            <tr key={item.id} className="hover:bg-indigo-50/20 group transition-all">
                                {config.fields.map(f => (
                                    <td key={f.name} className="px-8 py-4">
                                        {f.type === 'select' ? (
                                            <select 
                                                value={item[f.name]}
                                                className="w-full px-4 py-2.5 bg-transparent border-transparent border rounded-xl text-[12px] font-bold text-slate-600 focus:bg-white focus:border-indigo-100 outline-none transition-all cursor-pointer appearance-none"
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setItems(items.map(t => t.id === item.id ? { ...t, [f.name]: val } : t));
                                                    handleUpdate(item.id, { [f.name]: val });
                                                }}
                                            >
                                                <option value="">Select Option</option>
                                                {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        ) : (
                                            <div className="relative group/field">
                                                <input 
                                                    type={f.type || 'text'}
                                                    value={item[f.name]}
                                                    placeholder={f.label}
                                                    className={`w-full px-5 py-3 bg-transparent border border-transparent rounded-2xl text-[13px] font-bold text-slate-700 focus:bg-white focus:border-indigo-100 focus:ring-4 focus:ring-indigo-500/5 outline-none transition-all ${f.name === 'code' ? 'text-indigo-600 font-black' : ''}`}
                                                    onBlur={(e) => handleUpdate(item.id, { [f.name]: e.target.value })}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setItems(items.map(t => t.id === item.id ? { ...t, [f.name]: val } : t));
                                                    }}
                                                />
                                                <Edit3 className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-200 opacity-0 group-hover/field:opacity-100 transition-opacity" />
                                            </div>
                                        )}
                                    </td>
                                ))}
                                <td className="px-10 py-4 text-right">
                                    <button 
                                        onClick={() => handleDelete(item.id)}
                                        className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredItems.length === 0 && (
                    <div className="py-24 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Plus className="w-8 h-8 text-slate-200" />
                        </div>
                        <p className="text-[11px] font-black uppercase text-slate-300 tracking-widest">No matching records found</p>
                    </div>
                )}
            </div>
            
            <div className="mt-8 flex justify-center">
                <div className="bg-indigo-600/5 px-6 py-3 rounded-full border border-indigo-100 flex items-center gap-3">
                    <Check className="w-4 h-4 text-indigo-600" />
                    <p className="text-[10px] font-black text-indigo-900 uppercase tracking-widest">Changes are saved automatically as you type</p>
                </div>
            </div>
        </div>
    );
};

export default ClinicalMetadataSetup;
